import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  downloadShipmentPdf,
  useInternalShipment,
  useInternalShipmentMutations,
} from '../hooks/useInternalShipments';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Callout } from '../components/ui/Callout';
import { Kicker } from '../components/ui/Kicker';
import { Icon } from '../components/ui/Icon';
import { AlertDialog } from '../components/ui/radix/AlertDialog';

type Contact = {
  contactName: string;
  companyName?: string | null;
  email?: string | null;
  phone?: string | null;
  cityName?: string | null;
  state?: { name?: string | null } | null;
  countryName?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
};

const statusTone = (s: string): 'ok' | 'warn' | 'err' | 'info' | 'default' => {
  switch (s) {
    case 'delivered': return 'ok';
    case 'pending_review': return 'warn';
    case 'rejected':
    case 'cancelled': return 'err';
    case 'in_transit':
    case 'approved': return 'info';
    default: return 'default';
  }
};

const ContactBlock: React.FC<{ title: string; contact: Contact }> = ({ title, contact }) => (
  <Card pad>
    <Kicker>{title}</Kicker>
    <div style={{ fontWeight: 600, marginTop: 8 }}>{contact.contactName}</div>
    {contact.companyName && (
      <div className="muted" style={{ fontSize: 13 }}>{contact.companyName}</div>
    )}
    <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.6 }}>
      {contact.addressLine1 && <div>{contact.addressLine1}</div>}
      {contact.addressLine2 && <div>{contact.addressLine2}</div>}
      <div>
        {[contact.cityName, contact.state?.name, contact.countryName].filter(Boolean).join(', ')}
      </div>
    </div>
    {(contact.email || contact.phone) && (
      <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
        {contact.email && <div>{contact.email}</div>}
        {contact.phone && <div>{contact.phone}</div>}
      </div>
    )}
  </Card>
);

const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="mono" style={{ fontSize: 11, color: 'var(--ink-4)', letterSpacing: '0.06em' }}>
      {label.toUpperCase()}
    </div>
    <div style={{ fontSize: 14, fontWeight: 500, marginTop: 2 }}>{value}</div>
  </div>
);

const InternalShipmentDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [cancelOpen, setCancelOpen] = useState(false);
  const { shipment, isLoading, error } = useInternalShipment(id);
  const { cancelShipment } = useInternalShipmentMutations();

  if (isLoading) {
    return (
      <Page width="wide">
        <div className="muted" style={{ textAlign: 'center', padding: 64 }}>
          Loading shipment…
        </div>
      </Page>
    );
  }

  if (error || !shipment) {
    return (
      <Page width="wide">
        <Callout tone="warn">
          <Icon name="alertCircle" size={16} />
          <span>Failed to load the internal shipment.</span>
        </Callout>
      </Page>
    );
  }

  const handleCancel = async () => {
    await cancelShipment.mutateAsync(shipment.id);
    setCancelOpen(false);
  };

  return (
    <Page width="wide">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/internal-shipments')}
        style={{ marginBottom: 16 }}
      >
        <Icon name="arrowLeft" size={14} />
        <span>Back to internal shipments</span>
      </Button>

      <PageHead>
        <div>
          <Kicker>Shipment · {shipment.shipmentNumber}</Kicker>
          <PageTitle style={{ marginTop: 8 }}>
            {shipment.sender.contactName} → {shipment.recipient.contactName}
          </PageTitle>
          <PageSub>
            Created{' '}
            {shipment.createdAt
              ? format(new Date(shipment.createdAt), 'MMM d, yyyy h:mm a')
              : 'N/A'}
          </PageSub>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button size="sm" onClick={() => downloadShipmentPdf(shipment)}>
            <Icon name="download" size={14} />
            <span>Download PDF</span>
          </Button>
          {shipment.canEdit && (
            <Button
              size="sm"
              onClick={() => navigate(`/internal-shipments/new?edit=${shipment.id}`)}
            >
              <Icon name="edit" size={14} />
              <span>Edit</span>
            </Button>
          )}
          {shipment.canCancel && (
            <Button size="sm" variant="danger" onClick={() => setCancelOpen(true)}>
              <Icon name="x" size={14} />
              <span>Cancel</span>
            </Button>
          )}
        </div>
      </PageHead>

      <div className="grid-2" style={{ marginBottom: 20 }}>
        <ContactBlock title="Sender" contact={shipment.sender as unknown as Contact} />
        <ContactBlock title="Recipient" contact={shipment.recipient as unknown as Contact} />
      </div>

      <Card pad>
        <div
          className="row"
          style={{ justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}
        >
          <h3 style={{ fontSize: 16 }}>Shipment details</h3>
          <Badge tone={statusTone(shipment.status)} dot>{shipment.statusLabel}</Badge>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <DetailItem label="Item" value={shipment.itemDescription} />
          <DetailItem label="Weight" value={`${shipment.weightKg} kg`} />
          <DetailItem label="Quoted cost" value={`${shipment.currency} ${shipment.quotedCost}`} />
          <DetailItem label="Declared value" value={shipment.declaredValue ?? 'N/A'} />
          <DetailItem label="Carrier" value={shipment.carrier || 'Pending'} />
          <DetailItem label="Tracking" value={shipment.trackingNumber || 'Pending'} />
          <DetailItem
            label="Delivered at"
            value={
              shipment.deliveredAt
                ? format(new Date(shipment.deliveredAt), 'MMM d, yyyy h:mm a')
                : 'Not delivered'
            }
          />
        </div>
        {shipment.adminNotes && (
          <div
            style={{
              marginTop: 20,
              padding: 14,
              background: 'var(--bg-tint)',
              borderRadius: 8,
            }}
          >
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-4)', letterSpacing: '0.06em' }}>
              ADMIN NOTES
            </div>
            <div style={{ fontSize: 14, marginTop: 4 }}>{shipment.adminNotes}</div>
          </div>
        )}
      </Card>

      <AlertDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel internal shipment"
        description="This only works while the shipment is pending review. Continue?"
        confirmLabel={cancelShipment.isPending ? 'Cancelling…' : 'Cancel shipment'}
        cancelLabel="Keep shipment"
        onConfirm={handleCancel}
        destructive
      />
    </Page>
  );
};

export default InternalShipmentDetailsPage;
